import type { AssistantOverview, AssistantSuggestionItem, AssistantThreadView } from "@nmt/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { errorMessage, get, post } from "../api";
import { useSession } from "../session";
import { Alert, Badge, Button, Dialog, Empty, Loading, Panel, PanelHeader, TextAreaField, ago } from "./ui";

export const openFred = (conversationId: string) => window.dispatchEvent(new CustomEvent("nmt:open-fred", { detail: { conversationId } }));

/** One of Fred's suggestions: open it with Fred (to confirm or change what he proposed), see the texts, mark it done, or dismiss it. */
export function SuggestionRow({ s, compact = false }: { s: AssistantSuggestionItem; compact?: boolean }) {
  const qc = useQueryClient();
  const [dismissing, setDismissing] = useState(false);
  const [texts, setTexts] = useState(false);
  const [busy, setBusy] = useState(false);
  const refresh = () => void qc.invalidateQueries({ queryKey: ["assistant"] });
  return (
    <li className="flex flex-col gap-2 border-b border-mist px-5 py-3 last:border-0 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-ink">{s.title}</span>
          {s.waiting && <Badge tone="brand">Ready to confirm</Badge>}
        </div>
        {!compact && s.detail && <div className="text-sm text-ink-soft">{s.detail}</div>}
        <div className="text-xs text-muted">
          {s.who} · {ago(s.updatedAt)}
          {" · "}
          <button className="font-semibold text-brand hover:underline" onClick={() => setTexts(true)}>
            See the texts
          </button>
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        {s.conversationId && (
          <Button variant="primary" className="px-3 py-1" onClick={() => openFred(s.conversationId!)}>
            Open with Fred
          </Button>
        )}
        <Button
          className="px-3 py-1"
          busy={busy}
          onClick={async () => {
            setBusy(true);
            await post(`/api/assistant/suggestions/${s.id}/done`).catch(() => undefined);
            setBusy(false);
            refresh();
          }}
        >
          Done
        </Button>
        <Button className="px-3 py-1" onClick={() => setDismissing(true)}>
          Dismiss…
        </Button>
      </div>
      {dismissing && <DismissDialog s={s} onClose={() => (setDismissing(false), refresh())} />}
      {texts && <TextsDialog threadId={s.threadId} onClose={() => setTexts(false)} />}
    </li>
  );
}

function DismissDialog({ s, onClose }: { s: AssistantSuggestionItem; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [mute, setMute] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog
      title="Dismiss this suggestion"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            busy={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await post(`/api/assistant/suggestions/${s.id}/dismiss`, { reason });
                if (mute) await post(`/api/assistant/threads/${s.threadId}/mute`, { muted: true });
                onClose();
              } catch (err) {
                setError(errorMessage(err));
                setBusy(false);
              }
            }}
          >
            Dismiss
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="text-sm text-ink-soft">{s.title}</div>
        <TextAreaField label="Why? (optional)" hint="Fred uses this to learn what you want. Each Monday he proposes what he learned, and you keep or drop it." value={reason} onChange={(e) => setReason(e.target.value)} placeholder="I handle these myself; group chats aren't work…" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" className="size-4 accent-[var(--color-brand)]" checked={mute} onChange={(e) => setMute(e.target.checked)} />
          No more suggestions from this conversation with {s.who}
        </label>
        {error && <Alert>{error}</Alert>}
      </div>
    </Dialog>
  );
}

export function TextsDialog({ threadId, onClose }: { threadId: string; onClose: () => void }) {
  const q = useQuery({ queryKey: ["assistant-thread", threadId], queryFn: () => get<AssistantThreadView>(`/api/assistant/threads/${threadId}?limit=60`) });
  return (
    <Dialog title={q.data ? `Texts with ${q.data.who}` : "Texts"} onClose={onClose} footer={<Button onClick={onClose}>Close</Button>}>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <Alert>{errorMessage(q.error)}</Alert>
      ) : (
        <div className="flex max-h-[60vh] flex-col gap-1.5 overflow-y-auto">
          {q.data!.texts.map((t) => (
            <div key={t.id} className={t.fromMe ? "ml-10 self-end text-right" : "mr-10 self-start"}>
              {q.data!.isGroup && !t.fromMe && <div className="text-[11px] text-muted">{t.who}</div>}
              <div className={`rounded-[14px] px-3 py-1.5 text-sm whitespace-pre-wrap ${t.fromMe ? "bg-brand text-white" : "bg-mist text-ink"}`}>
                {t.text || "(attachment)"}
                {t.attachments > 0 && t.text ? " 📎" : ""}
              </div>
              <div className="text-[11px] text-muted">{new Date(t.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
            </div>
          ))}
          <p className="mt-2 text-xs text-muted">Only you can see these. Texts are kept 30 days, encrypted.</p>
        </div>
      )}
    </Dialog>
  );
}

/** The dashboard card: Fred's open suggestions from your texts (or how to connect your Mac). */
export function AssistantCard() {
  const { can } = useSession();
  const q = useQuery({ queryKey: ["assistant"], queryFn: () => get<AssistantOverview>("/api/assistant"), enabled: can("fred.use"), refetchInterval: 60_000 });
  if (!can("fred.use") || !q.data?.available) return null;
  const { suggestions, devices } = q.data;
  return (
    <Panel>
      <PanelHeader
        title="Fred's suggestions"
        description={devices.length ? "From texts on your phone. Only you see these." : undefined}
        action={
          <Link to="/assistant" className="text-sm font-semibold text-brand hover:underline">
            Assistant
          </Link>
        }
      />
      {q.isLoading ? (
        <Loading />
      ) : !devices.length ? (
        <Empty>
          Connect your Mac and Fred watches your texts for leads, plans, and to-dos. <Link to="/assistant" className="font-semibold text-brand hover:underline">Set it up</Link>
        </Empty>
      ) : !suggestions.length ? (
        <Empty>Nothing right now.</Empty>
      ) : (
        <ul className="max-h-[28rem] overflow-y-auto">
          {suggestions.slice(0, 8).map((s) => (
            <SuggestionRow key={s.id} s={s} compact />
          ))}
        </ul>
      )}
    </Panel>
  );
}
